import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Search } from "lucide-react";
import { getTournamentList, getTournamentDetail } from "../../api/tournaments";
import { ITournamentItem } from "../../models/ITournament";
import { useDebouncedSearch } from "../../hooks";
import { Button, Dialog, EmptyState, Loader, SearchInput } from "../../ui";
import "./styles.scss";

interface CreateFromExistingDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateFromExistingDialog = (props: CreateFromExistingDialogProps) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [tournaments, setTournaments] = useState<ITournamentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<ITournamentItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = useCallback(
    async (value: string) => {
      if (!value.trim()) {
        setTournaments([]);
        return;
      }

      setLoading(true);
      try {
        const res = await getTournamentList(1, 20, value, "name", "asc", [], [], [], [], "");
        setTournaments(res.data.items);
      } catch (error: any) {
        console.error("Error searching tournaments:", error.message);
        setTournaments([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const { searchValue: debouncedSearchValue, setSearchValue: setDebouncedSearchValue } = useDebouncedSearch(
    handleSearch,
    500,
    ""
  );

  useEffect(() => {
    if (props.isOpen) {
      setSearchValue("");
      setDebouncedSearchValue("");
      setTournaments([]);
      setSelectedTournament(null);
    }
  }, [props.isOpen, setDebouncedSearchValue]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setDebouncedSearchValue(value);
  };

  const handleSelectTournament = (tournament: ITournamentItem) => {
    setSelectedTournament(tournament);
  };

  const handleSubmit = async () => {
    if (!selectedTournament) return;

    setSubmitting(true);
    try {
      // Fetch full tournament details
      const { data: tournamentDetail } = await getTournamentDetail(selectedTournament.id);

      // Map the tournament data to AddTournamentDTO format (excluding dates)
      const prefilledData = {
        name: tournamentDetail.name || "",
        description: tournamentDetail.description || "",
        type: tournamentDetail.type || "",
        startDate: "",
        endDate: "",
        locationId: tournamentDetail.locationId || "",
        specificDates: [],
        cover: tournamentDetail.cover || "",
        tournamentFeatures:
          tournamentDetail.features?.map((f: { description?: string; id?: string }) => f.description || "") || [],
        seasonId: tournamentDetail.seasonId || "",
      };

      // Store in sessionStorage
      sessionStorage.setItem("prefilledTournament", JSON.stringify(prefilledData));

      props.onClose();

      // Navigate to add tournament page
      navigate("/dashboard/add-tournament");
    } catch (error: any) {
      console.error("Error fetching tournament details:", error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchValue("");
    setDebouncedSearchValue("");
    setTournaments([]);
    setSelectedTournament(null);
    props.onClose();
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      title="Create from Existing Tournament"
      bodyClassName="create-from-existing-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedTournament || submitting} loading={submitting}>
            {submitting ? "Loading..." : "Create"}
          </Button>
        </>
      }
    >
      <SearchInput
        block
        value={searchValue}
        onChange={handleSearchChange}
        placeholder="Search tournaments by name..."
        className="search-input"
      />

      {loading && <Loader minHeight={140} />}

      {!loading && searchValue.trim() && tournaments.length === 0 && (
        <EmptyState compact icon={<Search />} title="No tournaments found" />
      )}

      {!loading && tournaments.length > 0 && (
        <div className="tournaments-list">
          {tournaments.map((tournament) => {
            const isSelected = selectedTournament?.id === tournament.id;
            return (
              <button
                type="button"
                key={tournament.id}
                className={`tournament-item ${isSelected ? "selected" : ""}`}
                aria-pressed={isSelected}
                onClick={() => handleSelectTournament(tournament)}
              >
                <span className="tournament-info">
                  <span className="tournament-name">{tournament.name}</span>
                  <span className="tournament-details">
                    {tournament.type} • {tournament.location?.name || "-"}
                  </span>
                </span>
                {isSelected && <Check className="tournament-check" size={18} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}

      {!searchValue.trim() && <EmptyState compact icon={<Search />} title="Start typing to search for tournaments" />}
    </Dialog>
  );
};

export default CreateFromExistingDialog;

